import React, { useState, useEffect } from 'react';
import axios from '../../utils/axios';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Save, Send, ArrowRight, Plus, Trash2 } from 'lucide-react';

const PurchaseOrderDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isNew = id === 'new';

    const [order, setOrder] = useState(null);
    const [suppliers, setSuppliers] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);

    const [formData, setFormData] = useState({
        supplier_id: '',
        expected_date: '',
        notes: '',
        items: []
    });

    // Product search state
    const [productSearch, setProductSearch] = useState('');
    const [searchResults, setSearchResults] = useState([]);

    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                setLoading(true);
                const [suppliersRes, productsRes] = await Promise.all([
                    axios.get('/api/suppliers'),
                    axios.get('/api/products?limit=1000')
                ]);
                setSuppliers(suppliersRes.data.suppliers || suppliersRes.data);
                setProducts(productsRes.data.products || []);

                if (!isNew) {
                    const orderRes = await axios.get(`/api/purchase-orders/${id}`);
                    const po = orderRes.data.order || orderRes.data;
                    setOrder(po);
                    setFormData({
                        supplier_id: po.supplier_id,
                        expected_date: po.expected_date || '',
                        notes: po.notes || '',
                        items: (po.items || []).map(item => ({
                            product_id: item.product_id,
                            product_name: item.product?.name || '',
                            quantity: item.quantity,
                            unit_price: item.unit_price
                        }))
                    });
                }
            } catch (error) {
                toast.error('שגיאה בטעינת נתונים');
            } finally {
                setLoading(false);
            }
        };
        fetchInitialData();
    }, [id, isNew]);

    const handleAddItem = (product) => {
        setFormData(prev => ({
            ...prev,
            items: [
                ...prev.items,
                {
                    product_id: product.id,
                    product_name: product.name,
                    quantity: 1,
                    unit_price: product.cost_price || 0
                }
            ]
        }));
        setProductSearch('');
        setSearchResults([]);
    };

    const handleUpdateItem = (index, field, value) => {
        const newItems = [...formData.items];
        newItems[index][field] = value;
        setFormData({ ...formData, items: newItems });
    };

    const handleRemoveItem = (index) => {
        const newItems = formData.items.filter((_, i) => i !== index);
        setFormData({ ...formData, items: newItems });
    };

    const handleSave = async (status = 'DRAFT') => {
        try {
            const data = {
                ...formData,
                status
            };

            if (isNew) {
                await axios.post('/api/purchase-orders', data);
                toast.success('הזמנה נוצרה בהצלחה');
            } else {
                // Update logic (not fully implemented in backend yet for items, but basic update works)
                // For now, we might just update status if it's not draft
                if (status !== order.status) {
                    await axios.put(`/api/purchase-orders/${id}/status`, { status });
                }
                toast.success('הזמנה עודכנה');
            }
            navigate('/admin/purchase-orders');
        } catch (error) {
            toast.error('שגיאה בשמירת ההזמנה');
        }
    };

    const calculateTotal = () => {
        return formData.items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
    };

    useEffect(() => {
        if (productSearch.length > 1) {
            const results = products.filter(p =>
                p.name.toLowerCase().includes(productSearch.toLowerCase()) ||
                p.sku?.toLowerCase().includes(productSearch.toLowerCase())
            );
            setSearchResults(results.slice(0, 10));
        } else {
            setSearchResults([]);
        }
    }, [productSearch, products]);

    if (loading) return <div className="p-8 text-center">טוען...</div>;

    return (
        <div className="max-w-5xl mx-auto space-y-6" dir="rtl">
            <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4 space-x-reverse">
                    <button onClick={() => navigate('/admin/purchase-orders')} className="text-gray-500 hover:text-gray-700">
                        <ArrowRight className="w-6 h-6" />
                    </button>
                    <h1 className="text-2xl font-bold text-gray-900">
                        {isNew ? 'הזמנת רכש חדשה' : `הזמנה ${order?.order_number}`}
                    </h1>
                </div>
                <div className="flex space-x-3 space-x-reverse">
                    {(!order || order.status === 'DRAFT') && (
                        <>
                            <button
                                onClick={() => handleSave('DRAFT')}
                                className="flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                            >
                                <Save className="w-4 h-4 ml-2" />
                                שמור כטיוטה
                            </button>
                            <button
                                onClick={() => handleSave('SENT')}
                                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                            >
                                <Send className="w-4 h-4 ml-2" />
                                שלח לספק
                            </button>
                        </>
                    )}
                </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">ספק</label>
                        <select
                            value={formData.supplier_id}
                            onChange={(e) => setFormData({ ...formData, supplier_id: e.target.value })}
                            disabled={!isNew}
                            className="w-full border rounded-md px-3 py-2"
                        >
                            <option value="">בחר ספק</option>
                            {suppliers.map(s => (
                                <option key={s.id} value={s.id}>{s.name}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">צפי הגעה</label>
                        <input
                            type="date"
                            value={formData.expected_date}
                            onChange={(e) => setFormData({ ...formData, expected_date: e.target.value })}
                            disabled={!isNew && order?.status !== 'DRAFT'}
                            className="w-full border rounded-md px-3 py-2"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">הערות</label>
                        <input
                            type="text"
                            value={formData.notes}
                            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                            disabled={!isNew && order?.status !== 'DRAFT'}
                            className="w-full border rounded-md px-3 py-2"
                        />
                    </div>
                </div>

                <div className="border-t pt-6">
                    <h3 className="text-lg font-medium mb-4">פריטים</h3>

                    {(!order || order.status === 'DRAFT') && (
                        <div className="relative mb-4">
                            <input
                                type="text"
                                placeholder="חפש מוצר להוספה..."
                                value={productSearch}
                                onChange={(e) => setProductSearch(e.target.value)}
                                className="w-full border rounded-md px-3 py-2"
                            />
                            {searchResults.length > 0 && (
                                <div className="absolute z-10 w-full bg-white border rounded-md shadow-lg mt-1 max-h-60 overflow-auto">
                                    {searchResults.map(product => (
                                        <div
                                            key={product.id}
                                            onClick={() => handleAddItem(product)}
                                            className="p-2 hover:bg-gray-100 cursor-pointer flex justify-between"
                                        >
                                            <span>{product.name}</span>
                                            <span className="text-gray-500 text-sm">{product.sku}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">מוצר</th>
                                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 w-32">כמות</th>
                                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 w-32">מחיר יחידה</th>
                                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 w-32">סה"כ</th>
                                    {(!order || order.status === 'DRAFT') && <th className="px-4 py-2 w-10"></th>}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {formData.items.map((item, index) => (
                                    <tr key={index}>
                                        <td className="px-4 py-2 text-sm">{item.product_name}</td>
                                        <td className="px-4 py-2">
                                            <input
                                                type="number"
                                                min="1"
                                                value={item.quantity}
                                                onChange={(e) => handleUpdateItem(index, 'quantity', parseInt(e.target.value))}
                                                disabled={!isNew && order?.status !== 'DRAFT'}
                                                className="w-full border rounded px-2 py-1"
                                            />
                                        </td>
                                        <td className="px-4 py-2">
                                            <input
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                value={item.unit_price}
                                                onChange={(e) => handleUpdateItem(index, 'unit_price', parseFloat(e.target.value))}
                                                disabled={!isNew && order?.status !== 'DRAFT'}
                                                className="w-full border rounded px-2 py-1"
                                            />
                                        </td>
                                        <td className="px-4 py-2 text-sm font-medium">
                                            ₪{(item.quantity * item.unit_price).toLocaleString()}
                                        </td>
                                        {(!order || order.status === 'DRAFT') && (
                                            <td className="px-4 py-2 text-center">
                                                <button
                                                    onClick={() => handleRemoveItem(index)}
                                                    className="text-red-500 hover:text-red-700"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr className="bg-gray-50 font-bold">
                                    <td colSpan="3" className="px-4 py-3 text-left">סה"כ לתשלום:</td>
                                    <td className="px-4 py-3">₪{calculateTotal().toLocaleString()}</td>
                                    <td></td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PurchaseOrderDetail;
